import React, { ReactNode } from "react";

import styled, { css } from "styled-components";
import tw from "twin.macro";

import {
  PolymorphicComponentPropsWithRef,
  PolymorphicRef,
} from "../../utils/types";

interface BaseInputAdornmentProps {
  disablePointerEvents?: boolean;
}

const InputAdornmentBase = styled.div<{ $disablePointerEvents?: boolean }>`
  ${tw`tw-flex tw-items-center tw-mx-1 tw-gap-2`}

  ${(props) =>
    props.$disablePointerEvents &&
    css`
      ${tw`tw-pointer-events-none`}
    `}
`;

export type InputAdornmentProps<C extends React.ElementType> =
  PolymorphicComponentPropsWithRef<C, BaseInputAdornmentProps>;

export type InputAdornmentComponent = <C extends React.ElementType = "div">(
  props: InputAdornmentProps<C>
) => React.ReactElement | ReactNode | null;

export const InputAdornment: InputAdornmentComponent & {
  displayName?: string;
} = React.forwardRef(
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore - due to @node/types making ts more strict
  <C extends React.ElementType = "div">(
    { children, as, disablePointerEvents, ...rest }: InputAdornmentProps<C>,
    ref: PolymorphicRef<C>
  ) => {
    const Component = as || "div";
    return (
      <InputAdornmentBase
        {...rest}
        as={Component}
        $disablePointerEvents={disablePointerEvents}
        ref={ref}
      >
        {children}
      </InputAdornmentBase>
    );
  }
);

InputAdornment.displayName = "InputAdornment";
